/**
 * @file components/common/createDialog
 * @author zhangzhe(zhangzhe@baidu.com)
 */

import _ from 'lodash';
import classNames from 'classnames';
import React from 'react';
import {createPortal} from 'react-dom';
import {Modal, Button} from 'acud';

import {ConfigContext} from '../../common/ConfigProvider';
import {DU_PREFIX} from '../../_common/constants';

const CLS_PREFIX = `${DU_PREFIX}-dialog`;
export interface DialogOption {
    dialogType?: 'common' | 'plain'; // 枚举值
    className?: string;
    prefixCls?: string;
    title?: string;
    width?: number;
    maskClosable?: boolean;
    cancelWithSuccess?: boolean;
    foot?: boolean;
    okText?: string;
    cancelText?: string;
    payload?: any;
    message?: any;
    mask?: boolean; // 是否显示遮罩
    acudProps?: any; // acud属性
    customCancelHandler?: () => Promise<any>;
}

export interface DialogProps {
    open?: boolean;
    onSuccess?: (result?: any) => void;
    onCancel?: () => void;
}
const createDialog = (
    options: DialogOption,
    dialogOptionText: any
) => (WrappedComponent?: any): React.ComponentClass => {

    const {
        dialogType = 'common',
        className = '',
        prefixCls: customizePrefixCls,
        title,
        width,
        maskClosable,
        foot,
        okText,
        cancelText
    } = options;

    const {
        sure,
        cancel,
        submitting
    } = dialogOptionText;

    const {payload, message, cancelWithSuccess = false, mask = true, acudProps, customCancelHandler} = options;

    return class Dialog extends React.Component<DialogProps, any> {

        static getDerivedStateFromProps(nextProps: DialogProps) {
            if ('open' in nextProps) {
                return {open: nextProps.open};
            }
            return null;
        }

        static contextType = ConfigContext;

        private el: HTMLElement;
        private formRef: any;
        private commonFormRef: any;

        constructor(props: DialogProps) {
            super(props);

            this.commonFormRef = React.createRef();
            this.el = document.createElement('div');
            this.state = {
                confirmLoading: false,
                open: !!props.open,
                loadingText: submitting
            };
        }

        componentDidMount() {
            document.body.appendChild(this.el);
        }

        componentWillUnmount() {
            document.body.removeChild(this.el);
        }

        showDialog = () => {
            this.setState({open: true});
        };

        handleCancel = () => {
            this.setState({open: false});
            const {onCancel, onSuccess} = this.props;
            onCancel && onCancel();
            if (cancelWithSuccess && onSuccess) {
                onSuccess();
            }
        };

        saveRef = (form: any) => {
            this.formRef = form;
        };

        handleOk = () => {
            this.setState({confirmLoading: true});
            let submitFunc: Func | null = null;
            if (this.formRef && this.formRef.doSubmit) {
                submitFunc = this.formRef.doSubmit;
            }
            else if (this.formRef && this.formRef.getWrappedInstance()
                && this.formRef.getWrappedInstance().doSubmit) {
                submitFunc = this.formRef.getWrappedInstance().doSubmit;
            }
            else if (this.commonFormRef && this.commonFormRef.current
                && this.commonFormRef.current.doSubmit) {
                submitFunc = this.commonFormRef.current.doSubmit;
            }

            if (submitFunc) {
                submitFunc()
                    .then(() => this.setState({confirmLoading: false}))
                    .catch(() => this.setState({confirmLoading: false}));
            }
            else {
                // 关闭弹窗
                this.handleCancel();
            }
        };

        handleFormSuccess = (result?: any) => {
            const onSuccess = this.props.onSuccess;
            // 父组件传过来的成功后执行的方法，一般是列表刷新
            onSuccess && onSuccess(result);
            // 关闭弹窗
            this.handleCancel();
        };

        handleConfirmLoading = (loadingText?: string) => {
            this.setState({
                confirmLoading: true,
                loadingText
            });
        };

        handleCancelConfirmLoading = () => {
            this.setState({confirmLoading: false});
        };

        // zhangzhe 2020-12-16 为了支持自定义取消事件，满足aida删除操作想复用取消这个button的需求
        handleNewCancel = () => {
            if (_.isFunction(customCancelHandler)) {
                customCancelHandler().then(() => this.handleCancel());
            }
            else {
                this.handleCancel();
            }
        };

        render() {
            const {open, confirmLoading, loadingText} = this.state;

            const defaultPrefixCls = this.context.prefixCls;
            const prefixCls = customizePrefixCls
                || (defaultPrefixCls ? defaultPrefixCls + '-create-dialog' : '') || CLS_PREFIX;

            // footer配置
            let footerConfig: Array<React.ReactElement> | null = null;
            if (foot !== false) {
                if (dialogType === 'plain') {
                    footerConfig = [<Button key="ok" type="primary" onClick={this.handleCancel}>{sure}</Button>];
                }
                else {
                    footerConfig = [
                        <Button
                            key="cancel"
                            onClick={this.handleNewCancel}
                        >
                            {cancelText || cancel}
                        </Button>,
                        <Button
                            key="ok"
                            type="primary"
                            disabled={confirmLoading}
                            loading={confirmLoading}
                            onClick={this.handleOk}
                        >
                            {confirmLoading
                                ? (loadingText || submitting)
                                : (okText || sure)
                            }
                        </Button>
                    ];
                }
            }
            // 核心区域
            let main: React.ReactElement | null = null;
            if (WrappedComponent) {
                main = (
                    <WrappedComponent
                        wrappedComponentRef={this.saveRef}
                        ref={this.commonFormRef}
                        payload={payload}
                        onSuccess={this.handleFormSuccess}
                        onCancel={this.handleNewCancel}
                        onConfirmLoading={this.handleConfirmLoading}
                        onCancelConfirmLoading={this.handleCancelConfirmLoading}
                    />
                );
            }
            else if (message) {
                main = message;
            }

            const dialogClass = classNames(className, prefixCls);

            const dialogComp = (
                <Modal
                    className={dialogClass}
                    visible={open}
                    title={title}
                    width={width}
                    footer={footerConfig}
                    onCancel={this.handleNewCancel}
                    centered
                    destroyOnClose
                    maskClosable={maskClosable !== false}
                    mask={mask}
                    {...acudProps}
                >
                    {main ? main : null}
                </Modal>
            );
            return createPortal(
                dialogComp,
                this.el
            );
        }
    };
};

export default createDialog;
